import { useState } from 'react';
import SEOHelmet from '@/components/feature/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/comics';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9s8omee3oq4jqumu75g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const code = (parsed as { code?: string })?.code;
      const metaMessage = (parsed as { meta?: { message?: string } })?.meta?.message;
      const message = (parsed as { message?: string })?.message;
      const metaDetail = (parsed as { meta?: { detail?: string } })?.meta?.detail;
      const serverMsg = metaMessage || message || metaDetail || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(String(serverMsg || '提交失败，请稍后再试'));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <SEOHelmet
        title="关于我们 - 羞羞漫画 | 品牌介绍与联系方式"
        description="羞羞漫画是国内领先的正版成人漫画在线阅读平台，拥有5000+正版授权漫画，500万+注册用户。了解我们的品牌故事、核心优势与联系方式。"
        keywords="羞羞漫画,关于羞羞漫画,漫画平台,正版漫画,联系我们"
        canonicalUrl="https://www.xiuxiumanhua.com/about"
        pageType="webpage"
      />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="text-center py-10 md:py-14">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于{aboutInfo.brandName}
            </h1>
            <p className="text-sm text-foreground-500 max-w-lg mx-auto">{aboutInfo.subtitle}</p>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-14">
            {aboutInfo.stats.map((stat) => (
              <div key={stat.label} className="text-center p-5 bg-background-100 rounded-lg">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className={`${stat.icon} text-lg text-primary-500`}></i>
                </div>
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</p>
                <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </section>

          {/* Introduction */}
          <section className="mb-14">
            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">品牌故事</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                {aboutInfo.introduction}
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                <strong>我们的使命：</strong>{aboutInfo.mission}
              </p>
            </div>
          </section>

          {/* Features */}
          <section className="mb-14">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">核心优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {aboutInfo.features.map((feature) => (
                <div key={feature.title} className="flex gap-4 p-5 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-full">
                    <i className={`${feature.icon} text-lg text-primary-500`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-800 mb-1">{feature.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-14">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {aboutInfo.testimonials.map((testimonial, idx) => (
                <div key={idx} className="p-5 bg-background-100 rounded-lg">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    &ldquo;{testimonial.content}&rdquo;
                  </p>
                  <p className="text-xs font-medium text-foreground-800">{testimonial.name}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-14" id="contact">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-background-100 rounded-lg p-6">
                <h3 className="text-base font-heading font-semibold text-foreground-800 mb-4">联系方式</h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-full flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    {aboutInfo.contact.email}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-full flex-shrink-0">
                      <i className="ri-wechat-line text-accent-500"></i>
                    </div>
                    微信：{aboutInfo.contact.wechat}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-8 h-8 flex items-center justify-center bg-secondary-100 rounded-full flex-shrink-0">
                      <i className="ri-weibo-line text-secondary-500"></i>
                    </div>
                    {aboutInfo.contact.weibo}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-full flex-shrink-0">
                      <i className="ri-time-line text-primary-500"></i>
                    </div>
                    {aboutInfo.contact.workingHours}
                  </li>
                  <li className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-full flex-shrink-0">
                      <i className="ri-map-pin-line text-accent-500"></i>
                    </div>
                    {aboutInfo.contact.address}
                  </li>
                </ul>
              </div>

              <div className="bg-background-100 rounded-lg p-6">
                <h3 className="text-base font-heading font-semibold text-foreground-800 mb-4">在线留言</h3>
                <form
                  data-readdy-form
                  id="contact-form-about"
                  onSubmit={handleSubmit}
                >
                  <div className="honeypot-wrapper">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您想说的话（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送留言
                    </button>
                  </div>
                </form>

                {formStatus === 'success' && (
                  <div className="mt-4 p-3 bg-accent-100 text-accent-700 text-xs rounded-lg">
                    留言发送成功！我们会在24小时内回复您。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mt-4 p-3 bg-primary-100 text-primary-700 text-xs rounded-lg">
                    {formError}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}